const TABLE_ROW_KIND = 'table_row';

function directCells(row) {
  return Array.from(row.children).filter(element => element.matches('td, th'));
}

function freeTextIds(row) {
  return Array.from(row.querySelectorAll('[free-text]')).map(element =>
    element.getAttribute('free-text'),
  );
}

function sameValues(left, right) {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function tableById(root, tableId) {
  return Array.from(root.querySelectorAll('table[table-id]')).find(
    table => table.getAttribute('table-id') === tableId,
  );
}

function validCellRecord(cell) {
  return (
    cell &&
    typeof cell === 'object' &&
    typeof cell.freeTextId === 'string' &&
    cell.freeTextId.length > 0 &&
    Array.isArray(cell.pids) &&
    cell.pids.every(pid => typeof pid === 'string' && pid.length > 0)
  );
}

function validTableRowRecord(record) {
  return (
    record &&
    record.kind === TABLE_ROW_KIND &&
    record.version === 1 &&
    typeof record.tableId === 'string' &&
    record.tableId.length > 0 &&
    record.position &&
    record.position.type === 'after' &&
    Array.isArray(record.position.anchorFreeTextIds) &&
    record.position.anchorFreeTextIds.length > 0 &&
    record.position.anchorFreeTextIds.every(
      id => typeof id === 'string' && id.length > 0,
    ) &&
    Array.isArray(record.cells) &&
    record.cells.length > 0 &&
    record.cells.every(validCellRecord)
  );
}

/**
 * Split the single ordered_listitem JSON array by persistence kind. Legacy list
 * entries have no `kind`; newer, unrecognized kinds are deliberately inert.
 */
export function parseOrderedListitem(raw) {
  let entries;
  try {
    entries = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (_error) {
    entries = [];
  }
  if (!Array.isArray(entries)) entries = [];

  const listItems = [];
  const supplementalEntries = [];
  const tableRows = [];
  const unknownEntries = [];

  entries.forEach(entry => {
    if (!entry || typeof entry !== 'object' || !('kind' in entry)) {
      listItems.push(entry);
      return;
    }

    supplementalEntries.push(entry);
    if (entry.kind === TABLE_ROW_KIND) {
      tableRows.push(entry);
    } else {
      // Forward compatibility is fail-open for document loading: an older
      // client must keep numbering from the legacy entries even when a newer
      // client has added a structural kind it does not understand.
      unknownEntries.push(entry);
    }
  });

  return { listItems, supplementalEntries, tableRows, unknownEntries };
}

/** Keep existing list entries byte-for-JSON-value unchanged and append kinds. */
export function serializeOrderedListitem(listItems, supplementalEntries = []) {
  return JSON.stringify([
    ...(Array.isArray(listItems) ? listItems : []),
    ...(Array.isArray(supplementalEntries) ? supplementalEntries : []),
  ]);
}

/** Build the structural record for an accepted generated row insertion. */
export function tableRowRecordFromInsertion(marker, diffgramItem) {
  if (
    !marker?.matches('ins[data-table-row-change="insertion"]') ||
    !marker.classList.contains('diff-item--accepted') ||
    !diffgramItem
  ) {
    return null;
  }

  const row = marker.querySelector('tr');
  const table = marker.closest('table[table-id]');
  const anchorFreeTextIds = diffgramItem._rowAnchorFreeTextIds;
  if (!row || !table || !Array.isArray(anchorFreeTextIds)) return null;

  const cells = directCells(row).map(cell => {
    const freeText = cell.querySelector('[free-text]');
    return {
      freeTextId: freeText?.getAttribute('free-text') || '',
      pids: Array.from(cell.querySelectorAll('[pid]')).map(element =>
        element.getAttribute('pid'),
      ),
    };
  });
  const record = {
    kind: TABLE_ROW_KIND,
    version: 1,
    tableId: table.getAttribute('table-id'),
    position: {
      type: 'after',
      anchorFreeTextIds: [...anchorFreeTextIds],
    },
    cells,
  };

  return validTableRowRecord(record) ? record : null;
}

/** Add an accepted row once. Rejections and delete_row markers are inert. */
export function persistAcceptedTableRow(
  supplementalEntries,
  marker,
  diffgramItem,
) {
  const entries = Array.isArray(supplementalEntries) ? supplementalEntries : [];
  const record = tableRowRecordFromInsertion(marker, diffgramItem);
  if (!record) return entries;

  const cellIds = record.cells.map(cell => cell.freeTextId);
  const alreadyStored = entries.some(
    entry =>
      validTableRowRecord(entry) &&
      entry.tableId === record.tableId &&
      sameValues(
        entry.cells.map(cell => cell.freeTextId),
        cellIds,
      ),
  );
  return alreadyStored ? entries : [...entries, record];
}

function clearTemplateCellContent(cell) {
  const editable = cell.querySelector('.editable[section-id]');
  if (!editable) return;
  Array.from(editable.childNodes).forEach(node => {
    if (node.nodeType !== 1 || !node.matches('.SOCE, .EOCE')) {
      node.remove();
    }
  });
}

/**
 * Recreate accepted rows before projection. Cell contents are intentionally not
 * stored here: compiling each rebuilt `[free-text]` loads them through the
 * normal free-text persistence channel.
 */
export function rebuildPersistedTableRows(root, records) {
  if (!root || !Array.isArray(records)) return [];

  const rebuilt = [];
  const landingSites = new Map();

  records.forEach(record => {
    if (!validTableRowRecord(record)) return;
    const table = tableById(root, record.tableId);
    if (!table) return;

    const cellIds = record.cells.map(cell => cell.freeTextId);
    const existing = Array.from(table.querySelectorAll('tr')).find(row =>
      sameValues(freeTextIds(row), cellIds),
    );
    const landingKey = `${record.tableId}:${JSON.stringify(
      record.position.anchorFreeTextIds,
    )}`;
    if (existing) {
      landingSites.set(landingKey, existing);
      return;
    }

    const anchor = Array.from(table.querySelectorAll('tr')).find(row =>
      sameValues(freeTextIds(row), record.position.anchorFreeTextIds),
    );
    if (!anchor) return;

    const row = anchor.cloneNode(true);
    row.querySelectorAll('[id]').forEach(element => {
      element.removeAttribute('id');
    });
    const cells = directCells(row);
    if (cells.length !== record.cells.length) return;

    let usable = true;
    cells.forEach((cell, cellIndex) => {
      const cellRecord = record.cells[cellIndex];
      const freeText = cell.querySelector('[free-text]');
      const pids = Array.from(cell.querySelectorAll('[pid]'));
      if (!freeText || pids.length !== cellRecord.pids.length) {
        usable = false;
        return;
      }
      freeText.setAttribute('free-text', cellRecord.freeTextId);
      freeText.querySelectorAll('[section-id]').forEach(element => {
        element.setAttribute('section-id', cellRecord.freeTextId);
      });
      pids.forEach((element, pidIndex) => {
        element.setAttribute('pid', cellRecord.pids[pidIndex]);
      });
      clearTemplateCellContent(cell);
    });
    if (!usable) return;

    row.setAttribute('data-persisted-table-row', 'true');
    const landingSite = landingSites.get(landingKey) || anchor;
    landingSite.after(row);
    landingSites.set(landingKey, row);
    rebuilt.push(row);
  });

  return rebuilt;
}
